'use client';

import { useEffect, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import {
  Badge,
  Divider,
  LinkButton,
  PageError,
  PageHeader,
  PageLoading,
  Panel,
  Stat,
  StatGroup,
  Table,
  type BadgeTone,
  type Column,
} from '@/ui';
import { fmtCurrency, fmtDateTime, fmtWhen } from '@/lib/billing-ui';
import {
  portalApi,
  type PortalCharge,
  type PortalContract,
  type PortalDeal,
} from '@/lib/api/portal';
import { PortalRequestsSection } from './portal-requests-section';
import { usePortalSession } from './portal-shell';

// Customer-facing labels — softer than the internal ledger's.
const CHARGE_STATUS_DISPLAY: Record<string, { label: string; tone: BadgeTone }> = {
  succeeded: { label: 'Paid', tone: 'success' },
  scheduled: { label: 'Upcoming', tone: 'neutral' },
  processing: { label: 'Processing', tone: 'accent' },
  failed: { label: 'Payment due', tone: 'warning' },
  awaiting_recovery: { label: 'Payment due', tone: 'warning' },
  refunded: { label: 'Refunded', tone: 'neutral' },
};

function chargeWhen(charge: PortalCharge): string {
  if (charge.status === 'succeeded') return fmtWhen(charge.paidAt, { past: 'plain' });
  return fmtWhen(charge.nextAttemptAt, { past: 'plain' });
}

const CHARGE_COLUMNS: Column<PortalCharge>[] = [
  { key: 'when', header: 'When', render: (c) => <span className="tabular-nums">{chargeWhen(c)}</span>, noWrap: true },
  {
    key: 'description',
    header: 'Description',
    render: (c) => (
      <div>
        <div className="text-zinc-100">{c.description}</div>
        <div className="text-xs text-zinc-400">
          {[c.agreementLabel, c.sequenceLabel].filter(Boolean).join(' · ')}
        </div>
      </div>
    ),
  },
  { key: 'company', header: 'Billed by', render: (c) => c.operatingCompanyName, noWrap: true },
  {
    key: 'amount',
    header: 'Amount',
    align: 'right',
    render: (c) => <span className="tabular-nums">{fmtCurrency(c.amountCents, c.currency)}</span>,
    noWrap: true,
  },
  {
    key: 'status',
    header: 'Status',
    render: (c) => {
      const display = CHARGE_STATUS_DISPLAY[c.status] ?? { label: c.status, tone: 'neutral' as BadgeTone };
      return <Badge tone={display.tone}>{display.label}</Badge>;
    },
    noWrap: true,
  },
];

const CONTRACT_COLUMNS: Column<PortalContract>[] = [
  { key: 'subject', header: 'Contract', render: (c) => c.subject },
  { key: 'company', header: 'With', render: (c) => c.operatingCompanyName ?? '—', noWrap: true },
  {
    key: 'signed',
    header: 'Signed',
    render: (c) => <span className="tabular-nums">{fmtDateTime(c.signedAt)}</span>,
    noWrap: true,
  },
  {
    key: 'download',
    header: '',
    align: 'right',
    render: (c) =>
      c.hasPdf ? (
        <LinkButton href={`/api/portal/contracts/${c.id}/pdf`} variant="secondary" size="sm">
          Download PDF
        </LinkButton>
      ) : null,
    noWrap: true,
  },
];

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="text-sm font-semibold text-zinc-50">{children}</h2>;
}

// The whole portal on one page, per the product spec's "flattening all of it":
// products, the money ledger, contracts, and change requests in one scroll.
export function PortalDashboardPage() {
  const { contact } = usePortalSession();
  const [deals, setDeals] = useState<PortalDeal[] | null>(null);
  const [charges, setCharges] = useState<PortalCharge[] | null>(null);
  const [contracts, setContracts] = useState<PortalContract[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([portalApi.overview(), portalApi.charges(), portalApi.contracts()])
      .then(([dealRows, chargeRows, contractRows]) => {
        setDeals(dealRows);
        setCharges(chargeRows);
        setContracts(contractRows);
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load your dashboard'));
  }, []);

  if (error) return <PageError>{error}</PageError>;
  if (!deals || !charges || !contracts) return <PageLoading />;

  let paid = 0;
  let upcoming = 0;
  let due = 0;
  for (const charge of charges) {
    if (charge.status === 'succeeded') paid += charge.amountCents - (charge.refundedAmountCents ?? 0);
    else if (charge.status === 'scheduled') upcoming += charge.amountCents;
    else if (charge.status === 'failed' || charge.status === 'awaiting_recovery') due += charge.amountCents;
  }
  const currency = charges[0]?.currency ?? '';

  // Review Suite is its own product, not an add-on to the website — list it on
  // its own row (deduped across deals that share one tenant).
  const reviewSuites: { id: string; operatingCompanyName: string; url: string }[] = [];
  const seenSuite = new Set<string>();
  for (const deal of deals) {
    const url = deal.reviewSuiteLoginUrl ?? deal.reviewSuiteUrl;
    if (url && !seenSuite.has(url)) {
      seenSuite.add(url);
      reviewSuites.push({ id: deal.id, operatingCompanyName: deal.operatingCompanyName, url });
    }
  }

  return (
    <div className="grid gap-5">
      <PageHeader title={`Welcome${contact.name ? `, ${contact.name}` : ''}`} />

      <Panel title="Your products">
        {deals.length === 0 ? (
          <p className="text-sm text-zinc-400">Your products will appear here once set up.</p>
        ) : (
          <div className="grid gap-2.5">
            {deals.map((deal) => (
              <div key={deal.id} className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
                <span className="text-sm font-medium text-zinc-100">{deal.productName ?? 'Product'}</span>
                <Badge tone="success">Active</Badge>
                <span className="text-sm text-zinc-400">{deal.operatingCompanyName}</span>
              </div>
            ))}
            {reviewSuites.map((suite) => (
              <div key={`rs-${suite.id}`} className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
                <span className="text-sm font-medium text-zinc-100">Review Suite</span>
                <Badge tone="success">Active</Badge>
                <span className="text-sm text-zinc-400">{suite.operatingCompanyName}</span>
                <a
                  href={suite.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-accent-bright inline-flex items-center gap-1 text-sm transition-colors hover:underline"
                >
                  Open Review Suite
                  <ExternalLink size={13} aria-hidden />
                </a>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <div className="grid gap-3">
        <SectionTitle>Payments</SectionTitle>
        <StatGroup>
          <Stat label="Paid to date" value={fmtCurrency(paid, currency)} />
          <Stat label="Upcoming" value={fmtCurrency(upcoming, currency)} />
          <Stat label="Payment due" value={fmtCurrency(due, currency)} tone={due > 0 ? 'warning' : 'neutral'} />
        </StatGroup>
        <Table columns={CHARGE_COLUMNS} rows={charges} rowKey={(c) => c.id} empty="No payments yet." />
      </div>

      <Divider />

      <div className="grid gap-3">
        <SectionTitle>Contracts</SectionTitle>
        <Table columns={CONTRACT_COLUMNS} rows={contracts} rowKey={(c) => c.id} empty="No signed contracts yet." />
      </div>

      <Divider />

      <div className="grid gap-3">
        <SectionTitle>Requests</SectionTitle>
        <PortalRequestsSection deals={deals} />
      </div>
    </div>
  );
}
